import { useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { approveCountLine, refreshCountSales } from '../api/countSheets';
import type { ApproveCountLineInput, ApproveCountLineResult, ManagerCountLine } from '../types/countSheets';
import { approvalComparison, atApprovalText, countedText, differenceText, salesRefreshText, sameApproval } from '../utils/countSheets';
import { detectedWasteProblem } from '../utils/inventoryCount';
import { formatWasteQuantity } from '../utils/wasteReview';
import { Answers, FooterReason, Question } from './capture';
import { Modal } from './Modal';
import { ActionBar } from './ui/ActionBar';
import { CheckRow } from './ui/CheckRow';
import { useConfirm } from './ui/ConfirmDialog';

// F4 · aprobar UN insumo contado. Se aplica la diferencia DEL MOMENTO DEL CONTEO sobre la existencia
// de ahora (lo vendido después no se pierde). El servidor la recalcula al aprobar; aquí se muestra.

const errorCode = (error: unknown) => (error instanceof ApiError ? (error.payload as { error?: string } | undefined)?.error : undefined);
/** Estos rechazos significan que el renglón ya no está por aprobar: se recarga la lista. */
const STALE_CODES = new Set(['COUNT_SHEET_LINE_SUPERSEDED', 'COUNT_SHEET_LINE_NOT_SUBMITTED', 'COUNT_SHEET_LINE_NOT_FOUND']);
/** Aprobar esperaba ventas de Loyverse (sin sincronizar desde el conteo o de antes sin descontar): se revisan solas. */
const SALES_CODES = new Set(['COUNT_SALES_SYNC_STALE', 'COUNT_PENDING_SALES']);

/** Revisar las ventas no terminó bien: un rechazo con su mensaje, o no sabemos si terminó. */
export function refreshSalesErrorMessage(error: unknown): string {
  return error instanceof ApiError && error.status >= 400 && error.status < 500
    ? getErrorMessage(error, 'No fue posible revisar las ventas.')
    : 'No sabemos si terminó: Loyverse tardó demasiado o se perdió la conexión. Espera un momento e intenta otra vez.';
}

export function CountLineApproveModal({ line, storeId, onClose, onApproved, onStale, onSalesRefreshed, onRequestRecount }: {
  line: ManagerCountLine;
  /** Tienda de la lista: al revisar las ventas se descuenta solo ahí. */
  storeId: string;
  onClose: () => void;
  onApproved: (message: string) => void;
  onStale: (message: string) => void;
  /** Ya se revisaron las ventas: la bandeja se vuelve a leer y devuelve este insumo recalculado (null = ya no está). */
  onSalesRefreshed: () => Promise<ManagerCountLine | null>;
  /** P-078 (E · BK12): «Pedir recontar» desde aquí cuando la existencia quedaría negativa (abre el mismo flujo de la fila). */
  onRequestRecount?: () => void;
}) {
  const confirm = useConfirm();
  const [wasteText, setWasteText] = useState('');
  const [reason, setReason] = useState('');
  const [needsSyncAck, setNeedsSyncAck] = useState(false);
  const [syncAck, setSyncAck] = useState(false);
  const [supplyMessage, setSupplyMessage] = useState<string | null>(null);
  const [supplyAck, setSupplyAck] = useState(false);
  const [forceReason, setForceReason] = useState(false);
  const [pendingSalesMessage, setPendingSalesMessage] = useState<string | null>(null);
  // Las ventas se revisan solas UNA vez por ventana; después, solo si alguien lo pide («Revisar ventas otra vez»).
  const [salesChecked, setSalesChecked] = useState(false);
  const [checkingSales, setCheckingSales] = useState(false);
  const [salesNote, setSalesNote] = useState<{ text: string; problem: boolean } | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string>();
  const symbol = line.unit.symbol;
  const comparison = approvalComparison(line);
  const shortage = comparison?.direction === 'SHORTAGE' ? Math.abs(Number(comparison.difference)) : 0;
  const waste = shortage > 0 ? detectedWasteProblem(wasteText, shortage) : { quantity: null, problem: null };
  const needsReason = Boolean(comparison?.large) || forceReason;
  const cannotApplySurplus = comparison?.direction === 'SURPLUS' && line.atApproval?.costAvailable === false;
  const wouldGoNegative = Boolean(line.atApproval?.wouldGoNegative);
  const blocker = wouldGoNegative ? 'La existencia quedaría negativa: revisa el conteo o pide recontar.'
    : cannotApplySurplus ? 'Sin costo registrado no se puede agregar el sobrante.'
    : waste.problem
      ?? (needsReason && reason.trim().length < 10 ? 'La diferencia es grande: escribe por qué (al menos 10 caracteres).' : null)
      ?? (pendingSalesMessage ? 'Hay ventas de antes del conteo que no se pudieron descontar.' : null)
      ?? (needsSyncAck && !syncAck ? 'No se pudo revisar Loyverse: intenta otra vez o confirma.' : null)
      ?? (supplyMessage && !supplyAck ? 'Confirma lo de las compras o transferencias pendientes.' : null);
  const working = isBusy || checkingSales;
  const close = () => { if (!working) onClose(); };

  /**
   * Revisa las ventas: trae Loyverse, descuenta lo pendiente de la tienda y vuelve a leer este insumo.
   * Devuelve el insumo recalculado, o null si no se pudo revisar (el motivo queda escrito en la ventana).
   */
  const checkSales = async (): Promise<{ fresh: ManagerCountLine | null } | null> => {
    setCheckingSales(true);
    try {
      const result = await refreshCountSales(storeId);
      setSalesNote(salesRefreshText(result));
      // Si Loyverse ya quedó sincronizado después del conteo, el aviso ya no aplica.
      const lastSyncedAt = result.salesSync.lastSyncedAt;
      if (line.countedAt && lastSyncedAt && Date.parse(lastSyncedAt) >= Date.parse(line.countedAt)) { setNeedsSyncAck(false); setSyncAck(false); }
      setPendingSalesMessage(null);
      // No se deja aprobar hasta que llegue lo recalculado: la diferencia que se ve es la que se aplicaría.
      return { fresh: await onSalesRefreshed() };
    } catch (refreshError) {
      setSalesNote({ text: refreshSalesErrorMessage(refreshError), problem: true });
      return null;
    } finally {
      setCheckingSales(false);
    }
  };

  const retryCheck = async () => {
    if (working) return;
    setError(undefined);
    await checkSales();
  };

  /** Un rechazo al aprobar, como siempre: los avisos que se confirman salen UNA vez, como casilla. */
  const showApproveError = (approveError: unknown) => {
    const code = errorCode(approveError);
    const message = getErrorMessage(approveError, 'No fue posible aprobar el conteo.');
    if (code && STALE_CODES.has(code)) { onStale(message); return; }
    if (code === 'COUNT_SALES_SYNC_STALE') { setNeedsSyncAck(true); return; }
    if (code === 'COUNT_PENDING_SUPPLY') { setSupplyMessage(message); return; }
    if (code === 'COUNT_PENDING_SALES') { setPendingSalesMessage(message); return; }
    if (code === 'COUNT_REASON_REQUIRED') setForceReason(true);
    if (approveError instanceof ApiError && approveError.status >= 400 && approveError.status < 500) {
      setError(message);
    } else {
      // Aprobar es exactamente una vez: si ya se había aprobado, el reintento no lo aplica otra vez.
      setError('No sabemos si se aprobó: se perdió la conexión o el servidor no respondió. Toca «Aprobar» otra vez; si ya se había aprobado, no se aplica dos veces.');
    }
  };

  const submit = async () => {
    if (working || blocker) return;
    // Igual que al contar (F3): una diferencia grande pide una segunda confirmación, además del motivo.
    if (needsReason && comparison && !(await confirm({ title: 'La diferencia es grande', description: `${line.ingredient.name}: ${differenceText(comparison, symbol)}. ¿Aprobar el conteo?`, confirmLabel: 'Sí, aprobar', cancelLabel: 'Revisar' }))) return;
    setIsBusy(true);
    setError(undefined);
    const input: ApproveCountLineInput = {
      ...(waste.quantity ? { detectedWasteQuantity: waste.quantity } : {}),
      ...(needsReason && reason.trim() ? { largeDifferenceReason: reason.trim() } : {}),
      ...(syncAck ? { staleSalesSyncAcknowledged: true } : {}),
      ...(supplyAck ? { pendingSupplyAcknowledged: true } : {}),
    };
    try {
      onApproved(approvedMessage(await approveCountLine(line.id, input), line));
    } catch (approveError) {
      const code = errorCode(approveError);
      // Loyverse atrasado o ventas de antes sin descontar (decisión del dueño): se revisan las ventas SOLAS.
      // Si lo que se aplicaría no cambió, se aprueba en el mismo toque; si cambió (llegó un ticket de antes),
      // se muestra para que el gerente lo vea antes de aprobar. Nunca se aprueba en silencio otro número.
      if (code && SALES_CODES.has(code) && !salesChecked) {
        setSalesChecked(true);
        const checked = await checkSales();
        if (!checked) { showApproveError(approveError); return; }
        if (!sameApproval(line, checked.fresh)) {
          setSalesNote((current) => current && { text: `${current.text} Con eso cambió lo que se aplicaría: revísalo y toca «Aprobar» otra vez.`, problem: current.problem });
          return;
        }
        try {
          onApproved(approvedMessage(await approveCountLine(line.id, input), line));
        } catch (retryError) {
          showApproveError(retryError);
        }
        return;
      }
      showApproveError(approveError);
    } finally {
      setIsBusy(false);
    }
  };

  const late = atApprovalText(line);
  return (
    <Modal
      title={`Aprobar conteo de ${line.ingredient.name}`}
      description={`Contó ${line.countedBy?.name ?? 'alguien del equipo'}`}
      isBusy={working}
      onClose={close}
      // P-078 (ActionBar v2 · E): el motivo en su renglón y, cuando el bloqueo es «la existencia quedaría negativa», la
      // acción que lo resuelve («Pedir recontar», el mismo flujo de la fila) aquí mismo. Aprobar sigue deshabilitado igual.
      footer={<ActionBar aria-label="Acciones del conteo" note={blocker ? <FooterReason>{blocker}{wouldGoNegative && onRequestRecount ? <> <button className="link-button" disabled={working} onClick={onRequestRecount} type="button">Pedir recontar</button></> : null}</FooterReason> : undefined} primary={<button className="button button--primary" disabled={working || Boolean(blocker)} onClick={() => void submit()} type="button">{checkingSales ? 'Revisando ventas…' : isBusy ? 'Aprobando…' : 'Aprobar'}</button>}>
        <button className="text-button" disabled={working} onClick={close} type="button">Cancelar</button>
      </ActionBar>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <p>{countedText(line)}</p>
      {late && <div className="alert alert--warning" role="status">{late}</div>}
      {line.reportedWasteSameDay && line.reportedWasteSameDay.count > 0 && (
        <p className="wiz-hint">Ese día se registraron {line.reportedWasteSameDay.count} merma(s) de este insumo ({formatWasteQuantity(line.reportedWasteSameDay.quantity, symbol)}).</p>
      )}
      <p className="wiz-hint">Al aprobar se aplica la diferencia de la hora del conteo. Lo que se vendió o se mermó después ya está descontado y no se pierde.</p>
      {cannotApplySurplus && <div className="alert alert--warning" role="status">Sobra producto, pero este insumo no tiene costo registrado en esta tienda. Registra primero una compra o una entrada con costo.</div>}
      {shortage > 0 && <>
        <Question>¿Parte de lo que falta fue merma?</Question>
        <Answers>
          <label className="field">
            <span>Merma detectada en conteo, en {symbol} (opcional, hasta {formatWasteQuantity(String(shortage), symbol)})</span>
            <input inputMode="decimal" maxLength={14} onChange={(event) => { setWasteText(event.target.value); setError(undefined); }} placeholder="0" type="text" value={wasteText} />
          </label>
          <p className="wiz-hint">Se registra como merma «Detectada en conteo». El resto queda como diferencia de conteo.</p>
        </Answers>
      </>}
      {needsReason && <>
        <Question>La diferencia es grande: ¿por qué?</Question>
        <Answers>
          <label className="field">
            <span>Motivo (obligatorio, al menos 10 caracteres)</span>
            <textarea maxLength={300} onChange={(event) => { setReason(event.target.value); setError(undefined); }} placeholder="Ej. Se cayó una caja completa" rows={2} value={reason} />
          </label>
        </Answers>
      </>}
      {salesNote && <div className={`alert ${salesNote.problem ? 'alert--warning' : 'alert--success'}`} role="status">{salesNote.text}</div>}
      {pendingSalesMessage && <div className="alert alert--warning" role="status">{pendingSalesMessage}</div>}
      {/* Solo si revisar las ventas no bastó: Loyverse no respondió o algo no se pudo descontar. */}
      {(needsSyncAck || pendingSalesMessage) && (
        <div className="count-refresh">
          <button className="button button--secondary" disabled={working} onClick={() => void retryCheck()} type="button">{checkingSales ? 'Revisando ventas…' : 'Revisar ventas otra vez'}</button>
          <span className="wiz-hint">Trae los tickets nuevos de Loyverse y descuenta los de esta tienda.</span>
        </div>
      )}
      {/* P-076: renglón de selección con el texto completo (reconocimiento sobre inventario: no se acorta). */}
      {needsSyncAck && (
        <CheckRow checked={syncAck} className="count-sync-ack" label="No se pudo revisar Loyverse desde la hora del conteo: puede haber ventas de antes que todavía no llegan. Entiendo, aprobar de todos modos." onChange={setSyncAck} />
      )}
      {supplyMessage && (
        <CheckRow checked={supplyAck} className="count-sync-ack" label={supplyMessage} onChange={setSupplyAck} />
      )}
    </Modal>
  );
}

function approvedMessage(result: ApproveCountLineResult, line: ManagerCountLine): string {
  if (result.replayed) return `El conteo de ${line.ingredient.name} ya estaba aprobado; no se aplicó otra vez.`;
  if (result.status === 'matched') return `Aprobado: ${line.ingredient.name} cuadra, no se movió el inventario.`;
  const difference = Number(result.difference ?? 0);
  const magnitude = formatWasteQuantity(String(Math.abs(difference)), line.unit.symbol);
  return `Aprobado: se aplicó que ${difference < 0 ? 'faltaban' : 'sobraban'} ${magnitude} de ${line.ingredient.name}.`;
}
